import React, { useEffect, useState, useRef } from "react";
import InvoiceList from "./InvoiceList";
import Layout from "../../components/Layout";
import { months } from "../../utils/Helpers";
import { generateBankPayrollReport, generateInvoice, getMemberInvoices } from "../../services";
import _ from "lodash";
import { CSVLink } from "react-csv";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { FileText, CalendarDays, CalendarRange, Sparkles, Download, Landmark } from "lucide-react";
import CustomSelect from "../../components/CustomSelect";
import TaxSlabModal from "./TaxSlabModal";

export default function Invoice() {
  const userProfile = useSelector((state) => state.user.userinfo);
  const members = useSelector((state) => state.members.members);
  const isAdmin = userProfile?.MemberRole == 2;
  const [activeTab, setActiveTab] = useState("create");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBank, setisLoadingBank] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [isbankPayrollGenrated, setIsBankPayrollGenrated] = useState(false);
  const [bankPayroll, setBankPayroll] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(null);
  const [yaer, setYear] = useState("");
  const [TotalTaxRateAmount, setTotalTaxRateAmount] = useState(0);
  const [taxTotalFromApi, setTaxTotalFromApi] = useState(0);
  const [isLoadingCSV, setIsLoadingCSV] = useState(false);
  const [isTaxSlabOpen, setIsTaxSlabOpen] = useState(false);
  const csvLinkRef = useRef();
  const taxCsvLinkRef = useRef();

  const [errMsg, setErrMsg] = useState({
    err: false,
    msg: "",
    color: "warning",
  });

  const handleGenerate = async () => {
    setInvoices([])
    setErrMsg({ err: false, msg: "", color: "red" });
    if (!selectedMonth) {
      setErrMsg({ err: true, msg: "please select the month.", color: "red" });

      return;
    }
    setIsBankPayrollGenrated(false);
    setBankPayroll(null);
    setPayrollMonth(null);
    const invoiceData = {
      invoiceNo: "",
      month: parseInt(selectedMonth),
      year: parseInt(yaer),
      isRegenerate: false,
      changes: {},
    };

    setIsLoading(true);
    try {
      const response = await generateInvoice(invoiceData);
      if (response.data?.Result?.Invoices) {
        setInvoices(response.data.Result.Invoices);
        setTotalTaxRateAmount(response.data.Result.TotalTaxRateAmount || 0); 

        setPayrollMonth(invoiceData.month);
      } else {
        console.error("Unexpected response structure", response);
      }
    } catch (error) {
      console.error("Error generating invoice", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleBankGenerate = async () => {
    setisLoadingBank(true);
    setIsBankPayrollGenrated(false);
    setBankPayroll(null);
    try {
      const response = await generateBankPayrollReport(payrollMonth);

      if (response?.data?.Message === "Success") {

        setIsBankPayrollGenrated(true);
        setBankPayroll(response?.data?.Result?.filePath?.Result?.BankPayroll);
      } else {
        setBankPayroll(null);
        setIsBankPayrollGenrated(false);
        console.error("No approved invoice available in current month");
      }
    } catch (error) {
      console.error("Error generating invoice", error);
      setBankPayroll(null);
      setIsBankPayrollGenrated(false);
    } finally {
      setisLoadingBank(false);
    }
  };
  useEffect(() => {
    if (!userProfile?.Id) return;

    const now = new Date();
    const liveMonth = now.getMonth() + 1;
    const liveYear = now.getFullYear();
    const defaultMonth = liveMonth === 1 ? 12 : liveMonth - 1;
    const defaultYear = liveMonth === 1 ? liveYear - 1 : liveYear;

    // defaults (only if not already set)
    setSelectedMonth((prev) => prev || defaultMonth.toString());
    setYear((prev) => prev || defaultYear.toString());

    const fetchForTab = async () => {
      setIsLoading(true);
      try {
        const monthParam = selectedMonth || defaultMonth.toString();
        const yearParam = yaer || defaultYear.toString();

        // Admin tabs
        if (isAdmin && activeTab === "invoices") {
          const response = await getMemberInvoices(userProfile.Id, 0, 100);
          const list = response.data?.Result?.InvoiceList || [];
          // Invoices tab should always show logged-in member's invoices only.
          const ownInvoices = list.filter((inv) => {
            const byMemberId = inv?.MemberId && Number(inv.MemberId) === Number(userProfile.Id);
            const byAltId = inv?.Id && Number(inv.Id) === Number(userProfile.Id);
            const byName =
              (inv?.MemberName || "").toLowerCase() === (userProfile?.MemberName || "").toLowerCase();
            return byMemberId || byAltId || byName;
          });
          setInvoices(ownInvoices);
          setTaxTotalFromApi(0);
          return;
        }

        if (isAdmin && activeTab === "tax") {
          // Tax tab uses member-invoices API with month/year filters.
          // MemberId intentionally empty so backend role-based logic applies.
          const response = await getMemberInvoices("", 0, 100, monthParam, yearParam);
          const result = response?.data?.Result || {};
          setInvoices(result?.InvoiceList || []);
          setTaxTotalFromApi(Number(result?.TotalTaxAmount || 0));
          return;
        }

        if (isAdmin && activeTab === "create") {
          // Create tab should never auto-generate invoices on load or selection changes.
          setInvoices((prev) => prev || []);
          setTaxTotalFromApi(0);
          return;
        }

        // Non-admin default
        const response = await getMemberInvoices(userProfile.Id, 0, 100);
        setInvoices(response.data?.Result?.InvoiceList || []);
        setTaxTotalFromApi(0);
      } catch (error) {
        console.error("Error fetching invoices", error);
        setInvoices([]);
        setTaxTotalFromApi(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForTab();
  }, [isAdmin, userProfile?.Id, activeTab, selectedMonth, yaer]);

  useEffect(() => {
    if (isAdmin && activeTab === "create") {
      setInvoices([]);
      setIsBankPayrollGenrated(false);
      setBankPayroll(null);
      setPayrollMonth(null);
    }
  }, [isAdmin, activeTab]);
  let totalAmount
  if (!_.isEmpty(invoices)) {
    totalAmount = invoices
      .filter((invoice) => invoice.InvoiceStatus === 3)
      .reduce((sum, invoice) => sum + parseFloat(invoice.PayableAmount), 0);
  }

  const prepareCSVData = () => {
    if (!invoices || !Array.isArray(invoices)) {
      return [];
    }
    return invoices.map((invoice) => ({
      MemberName: invoice.MemberName || "",
      Salary: invoice.Salary?.toString() || "0",
      PayableAmount: invoice.PayableAmount?.toString() || "0"
    }));
  };

  const headers = [
    { label: "Member Name", key: "MemberName" },
    { label: "Salary", key: "Salary" },
    { label: "Payable Amount", key: "PayableAmount" }
  ];

  const handleCsvDownload = () => {
    setIsLoadingCSV(true);
    setTimeout(() => {
      csvLinkRef.current.link.click();
      setIsLoadingCSV(false);
    }, 1000);
  };

  const prepareTaxCSVData = () => {
    return taxRows.map((row) => ({
      Name: row.name || "",
      CNIC: row.cnic || "",
      Salary: row.salary?.toString() || "0",
      MonthTax: row.monthTax?.toString() || "0",
    }));
  };

  const taxCsvHeaders = [
    { label: "Name", key: "Name" },
    { label: "CNIC", key: "CNIC" },
    { label: "Salary", key: "Salary" },
    { label: "Month Tax", key: "MonthTax" },
  ];

  const handleTaxCsvDownload = () => {
    setIsLoadingCSV(true);
    setTimeout(() => {
      taxCsvLinkRef.current.link.click();
      setIsLoadingCSV(false);
    }, 700);
  };

  const yearOptions = Object.fromEntries(
    Array.from({ length: 11 }, (_, i) => {
      const year = (2025 + i).toString();
      return [year, year];
    })
  );

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const selectedYearNum = Number(yaer || currentYear);

  const disabledYearOptions = Object.fromEntries(
    Object.keys(yearOptions).map((year) => [year, Number(year) > currentYear])
  );

  const maxEnabledMonth = selectedYearNum < currentYear ? 12 : Math.max(currentMonth - 1, 0);
  const disabledMonthOptions = Object.fromEntries(
    Object.keys(months).map((month) => [month, Number(month) > maxEnabledMonth])
  );
  const memberByName = (members || []).reduce((acc, m) => {
    acc[(m?.MemberName || "").toLowerCase()] = m;
    return acc;
  }, {});

  const taxRows = (invoices || []).map((invoice, index) => {
    const member = memberByName[(invoice?.MemberName || "").toLowerCase()] || {};
    const cnic = invoice?.CNIC || invoice?.cnic || member?.CNIC || member?.cnic || "";
    const salary = Number(invoice?.Salary ?? invoice?.Salary ?? member?.Salary ?? 0);
    const monthTax = Number(invoice?.TaxAmount ?? 0);
    return {
      sr: index + 1,
      name: invoice?.MemberName || "",
      cnic,
      salary,
      monthTax,
    };
  });
  const computedTaxTotal = taxRows.reduce((sum, row) => sum + row.monthTax, 0);
  const totalTax = isAdmin && activeTab === "tax" && taxTotalFromApi > 0 ? taxTotalFromApi : computedTaxTotal;

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 sm:px-6 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 rounded-xl shrink-0">
            <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Invoice</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
              {isAdmin
                ? "Generate payroll invoices, bank reports, and exports for your team"
                : "View your salary invoices"}
            </p>
          </div>
        </div>
      </motion.div>
      {isAdmin && (
        <div className="px-4 sm:px-6 mb-4">
          <div className="w-full rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-white/60 dark:bg-black/30 backdrop-blur-xl p-1">
            {[
              { key: "invoices", label: "Invoices" },
              { key: "create", label: "Create Invoice" },
              { key: "tax", label: "Tax" },
            ].map((t) => {
              const on = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`w-1/3 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    on
                      ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100/70 dark:hover:bg-white/10"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && activeTab === "create" && (
        <div className="px-4 sm:px-6 mb-4">
          <div className="rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-white/70 dark:bg-black/30 backdrop-blur-xl p-4 sm:p-5">
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(260px,360px)] gap-4 items-start">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Month
                  </label>
                  <CustomSelect
                    options={months}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    placeholder="Select Month"
                    icon={CalendarDays}
                    disabledOptions={disabledMonthOptions}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Year
                  </label>
                  <CustomSelect
                    options={yearOptions}
                    value={yaer}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="Select Year"
                    icon={CalendarRange}
                    disabledOptions={disabledYearOptions}
                  />
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    className={`w-full h-[48px] inline-flex items-center justify-center gap-2 px-6 rounded-xl text-sm font-bold transition-all ${
                      isLoading
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20"
                    }`}
                    onClick={handleGenerate}
                    disabled={isLoading || !selectedMonth || !yaer}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          aria-hidden="true"
                          className="w-4 h-4 animate-spin"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 min-w-0">
                <div className="rounded-xl border border-gray-200 dark:border-blue-gray-900 bg-gray-50 dark:bg-gray-900/40 p-3.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Total Payable Amount
                  </p>
                  <p className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    {_.isEmpty(invoices) ? "0" : totalAmount}
                  </p>
                  {!_.isEmpty(invoices) && (
                    <button
                      type="button"
                      className={`mt-2 inline-flex items-center gap-1.5 text-sm font-semibold ${
                        isLoadingBank
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      }`}
                      onClick={handleBankGenerate}
                      disabled={isLoadingBank}
                    >
                      {isLoadingBank ? (
                        <>
                          <svg
                            aria-hidden="true"
                            className="w-4 h-4 animate-spin"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Landmark className="w-4 h-4" />
                          Generate Bank Payroll
                        </>
                      )}
                    </button>
                  )}

                
                </div>
              </div>
            </div>

            {isbankPayrollGenrated && (
              <div className="mt-3">
                {bankPayroll == null ? (
                  <div className="rounded-xl border border-amber-300/40 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-300">
                    No approved invoice is available for the selected month.
                  </div>
                ) : (
                  <div className="rounded-xl border border-blue-300/40 dark:border-blue-500/30 bg-blue-50/70 dark:bg-blue-500/10 px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300">
                    Bank payroll generated successfully.{" "}
                    <Link
                      to={`https://portalbackend.gentrickstaffing.com/bankpayroll/${bankPayroll}`}
                      className="font-bold underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-200"
                    >
                      Click here to download
                    </Link>
                    .
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {isAdmin && activeTab === "tax" && (
        <div className="px-4 sm:px-6 mb-4">
          <div className="rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-white/70 dark:bg-black/30 backdrop-blur-xl p-4">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-[560px] w-full">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Month
                  </label>
                  <CustomSelect
                    options={months}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    placeholder="Select Month"
                    icon={CalendarDays}
                    disabledOptions={disabledMonthOptions}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Year
                  </label>
                  <CustomSelect
                    options={yearOptions}
                    value={yaer}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="Select Year"
                    icon={CalendarRange}
                    disabledOptions={disabledYearOptions}
                  />
                </div>
              </div>

              <div className="h-[46px] flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleTaxCsvDownload}
                  disabled={isLoadingCSV || taxRows.length === 0}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className={`w-4 h-4 ${isLoadingCSV ? "animate-bounce" : ""}`} />
                  {isLoadingCSV ? "Preparing..." : "Download CSV"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsTaxSlabOpen(true)}
                  className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 transition-colors"
                >
                  tax_slab
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-300 dark:border-gray-700">
              <table className="min-w-[760px] w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr className="text-left">
                    <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700 w-14">#</th>
                    <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">Name</th>
                    <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">CNIC</th>
                    <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700 text-right">Salary</th>
                    <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700 text-right">Month Tax</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-black/20">
                  {taxRows.length > 0 ? (
                    taxRows.map((row) => (
                      <tr key={`${row.name}-${row.sr}`} className="border-b border-gray-200 dark:border-gray-800">
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.sr}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-semibold">{row.name}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono">{row.cnic || "-"}</td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-white tabular-nums">{row.salary.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-white tabular-nums">{row.monthTax.toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No tax records found.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <td className="px-3 py-2 border-t border-gray-300 dark:border-gray-700" />
                    <td className="px-3 py-2 border-t border-gray-300 dark:border-gray-700" />
                    <td className="px-3 py-2 border-t border-gray-300 dark:border-gray-700 text-right font-black text-gray-900 dark:text-white">
                      Total Tax
                    </td>
                    <td className="px-3 py-2 border-t border-gray-300 dark:border-gray-700" />
                    <td className="px-3 py-2 border-t border-gray-300 dark:border-gray-700 text-right font-black text-gray-900 dark:text-white tabular-nums">
                      {totalTax.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      <TaxSlabModal
        isOpen={isTaxSlabOpen}
        onClose={() => setIsTaxSlabOpen(false)}
      />

      {!(isAdmin && activeTab === "tax") && (
        <InvoiceList
          invoices={invoices}
          setInvoices={setInvoices}
          isbankPayrollGenrated={isbankPayrollGenrated}
          bankPayroll={bankPayroll}
          forceMemberView={isAdmin && activeTab === "invoices"}
        />
      )}

      <CSVLink
        data={prepareCSVData()}
        headers={headers}
        filename={`invoices_${selectedMonth || 'all'}_${yaer || new Date().getFullYear()}.csv`}
        ref={csvLinkRef}
        className="hidden"
        target="_blank"
      />
      <CSVLink
        data={prepareTaxCSVData()}
        headers={taxCsvHeaders}
        filename={`tax_report_${selectedMonth || "all"}_${yaer || new Date().getFullYear()}.csv`}
        ref={taxCsvLinkRef}
        className="hidden"
        target="_blank"
      />
    </Layout>
  );
}
